import {
	RangeText,
	CLOSE_ANNOTATION_HELPERS,
} from '@mission.io/question-toolkit'
import { getDistanceOfPointFromRectangle, Point } from '../../utils/math'

/**
 * getSelectableCharacterClosestToPoint - get the index of the markdown character closest to the given point
 *
 * @param {Point} point - the location to find closest character to
 * @param {Element} container - the DOM element containing the rendered markdown
 * @param {Array<RangeText>} selectableElements - the ranges of indices in markdown which contain selectable characters
 * @param {number} maxDistance - the maximum distance the nearest selectable character can be away from the point
 * @return
 */
export function getSelectableCharacterClosestToPoint(
	point: Point,
	container: Element,
	selectableElements: Array<RangeText>,
	maxDistance: number
): {
	node: ChildNode
	character: string
	characterIndex: number
	markdownIndex: number
	distance: number
} | null {
	const containersToCheck: Array<ItemWithDistance> = [
		{
			element: container,
			distance: getDistanceOfPointFromRectangle(
				point,
				container.getBoundingClientRect()
			),
		},
	] // If this is too slow, it can be changed to a min heap

	let closestDistanceText: {
		distance: number // the distance to the closest selectable text found so far
		character: string // the character that is the current closest
		node: ChildNode // the html ChildNode which contains the text which is the closest
		characterIndex: number // the index of the character in the node's textContent
		markdownIndex: number // the index of the character in the markdown text that has the current closest character to the point
	} | null = null

	for (
		let currentClosestContainer = getMinDistanceItem(containersToCheck);
		currentClosestContainer != null;
		currentClosestContainer = getMinDistanceItem(containersToCheck)
	) {
		if (
			(closestDistanceText &&
				currentClosestContainer.distance > closestDistanceText.distance) ||
			currentClosestContainer.distance > maxDistance
		) {
			// all other items are further away than the closest item
			break
		}

		for (const childNode of currentClosestContainer.element.childNodes) {
			if (childNode.nodeType === Node.TEXT_NODE) {
				const textContent = childNode.textContent
				if (textContent == null || textContent.length === 0) {
					continue
				}

				const markdownStartIndex = getNodeMarkdownStartIndex(childNode)
				if (markdownStartIndex == null) {
					// nothing selectable in this element, skip
					continue
				}

				const nodeMarkdownRange = {
					start: markdownStartIndex,
					end: markdownStartIndex + textContent.length,
				}
				const markdownRangesCoveredByNode = CLOSE_ANNOTATION_HELPERS.getIndicesOfClosestSelectableElements(
					nodeMarkdownRange,
					selectableElements
				)

				if (markdownRangesCoveredByNode == null) {
					// nothing can be selected in this node
					continue
				}

				const range = document.createRange()

				for (
					let selectableRangeIndex =
						markdownRangesCoveredByNode.firstElementIndex;
					selectableRangeIndex <= markdownRangesCoveredByNode.lastElementIndex;
					selectableRangeIndex += 1
				) {
					const selectableElementRange =
						selectableElements[selectableRangeIndex]
					if (!selectableElementRange) {
						// check needed due to typescript, this should not happen
						break
					}

					for (
						let characterIndex = Math.max(
							selectableElementRange.range.start - nodeMarkdownRange.start, // this translates the start of the selectable markdown range to an index in the textContent in the current html node
							0
						);
						characterIndex <
						Math.min(
							selectableElementRange.range.end - nodeMarkdownRange.start, // this translates the end of the selectable markdown range to an index in the textContent in the current html node
							textContent.length // make sure to stop checking before running out of characters in the current text node
						);
						characterIndex += 1
					) {
						// This can be optimized by knowing what line we are on
						const character = textContent.charAt(characterIndex)
						if (character == null) {
							continue
						}

						range.setStart(childNode, characterIndex)
						range.setEnd(childNode, characterIndex + 1)

						const distance = getDistanceOfPointFromRectangle(
							point,
							range.getBoundingClientRect()
						)

						if (
							closestDistanceText == null ||
							distance <= closestDistanceText.distance
						) {
							closestDistanceText = {
								character,
								distance,
								node: childNode,
								characterIndex: characterIndex,
								markdownIndex: nodeMarkdownRange.start + characterIndex,
							}

							if (distance === 0) {
								return closestDistanceText
							}
						}
					}
				}
			}
		}

		for (const child of currentClosestContainer.element.children) {
			const distance = getDistanceOfPointFromRectangle(
				point,
				child.getBoundingClientRect()
			)

			if (
				distance > maxDistance ||
				(closestDistanceText && distance > closestDistanceText.distance)
			) {
				// no way for the text to be closer than it already is
				continue
			}
			containersToCheck.push({
				element: child,
				distance,
			})
		}
	}

	if (closestDistanceText && closestDistanceText.distance < maxDistance) {
		return closestDistanceText
	}

	return null
}

type ItemWithDistance = {
	element: Element
	distance: number
}
/**
 * getMinDistanceItem - get the item with the minimum distance
 *
 * @param {Array<ItemWithDistance>}itemsWithDistance - [MUTATES] a list of items with their distance
 *
 * @return ItemWithDistance | null - the item with the minimum distance in the list (removed from the `itemsWithDistance` array)
 */
function getMinDistanceItem(
	itemsWithDistance: Array<ItemWithDistance>
): ItemWithDistance | null {
	const firstItem = itemsWithDistance[0]
	if (!itemsWithDistance.length || !firstItem) {
		return null
	}

	let closestItem: {
		index: number
		item: ItemWithDistance
	} = {
		index: 0,
		item: firstItem,
	}

	itemsWithDistance.forEach((item, index) => {
		if (item.distance < closestItem.item.distance) {
			closestItem = {
				index,
				item,
			}
		}
	})

	itemsWithDistance.splice(closestItem.index, 1)

	return closestItem.item
}

/**
 * getLocationOfMarkdownCharacter - get the location (DOMRect) of the character with the given markdown index on the page
 *
 * @param {Element} container - the Html element which contains the markdown
 * @param {number} markdownIndex - the markdown index of the character to get the position for
 *
 * @return DOMRect | null - the bounding box of the character on the page, or null if the character could not be found
 */
export function getLocationOfMarkdownCharacter(
	container: Element,
	markdownIndex: number
): DOMRect | null {
	const unknownQueue: Array<Element> = [container] // elements which may or may not contain the wanted element (this can easily happen in code blocks)
	const knownQueue: Array<Element> = [] // elements which have ranges indicated they contain the wanted element

	let currentElement
	while ((currentElement = knownQueue.pop() ?? unknownQueue.pop())) {
		for (const child of currentElement.children) {
			const childMarkdownStartIndexString = child?.attributes?.getNamedItem(
				'x-mission-io-annotation-start-index'
			)?.nodeValue
			const childMarkdownEndIndexString = child?.attributes?.getNamedItem(
				'x-mission-io-annotation-end-index'
			)?.nodeValue

			if (
				childMarkdownStartIndexString == null ||
				childMarkdownEndIndexString == null
			) {
				// we do not know what this contains, look at its children later
				unknownQueue.push(child)
				continue
			}

			const childMarkdownStartIndex: number = Number.parseInt(
				childMarkdownStartIndexString
			)
			const childMarkdownEndIndex: number = Number.parseInt(
				childMarkdownEndIndexString
			)
			if (
				Number.isNaN(childMarkdownStartIndex) ||
				!Number.isFinite(childMarkdownStartIndex) ||
				childMarkdownStartIndex < 0 ||
				Number.isNaN(childMarkdownEndIndex) ||
				!Number.isFinite(childMarkdownEndIndex) ||
				childMarkdownEndIndex < 0
			) {
				// malformed, may or may not contain the wanted value, look at its children later
				unknownQueue.push(child)
				continue
			}

			if (
				markdownIndex < childMarkdownStartIndex ||
				markdownIndex > childMarkdownEndIndex
			) {
				// does not contain the wanted element
				continue
			}

			if (child.children.length) {
				// children may contain the wanted element
				knownQueue.push(child)
				continue
			}

			// check text in node
			const possibleTextNode = child.childNodes[0]
			if (
				!possibleTextNode ||
				possibleTextNode.nodeType !== possibleTextNode.TEXT_NODE
			) {
				// no text node
				continue
			}
			const childTextIndex = markdownIndex - childMarkdownStartIndex

			const textContent = possibleTextNode.textContent
			if (!textContent || childTextIndex >= textContent.length) {
				// not in this node
				continue
			}

			const range = document.createRange()
			range.setStart(possibleTextNode, childTextIndex)
			range.setEnd(possibleTextNode, childTextIndex + 1)

			return range.getBoundingClientRect()
		}
	}

	return null
}

/**
 * findAnnotationRootElement - given a DOM element, find the annotation root element above it
 *
 * @param {Element} element - the element to find the annotation root element above
 *
 * @return Element | null - the annotation root element, or null if none exist
 */
export function findAnnotationRootElement(element: Element): Element | null {
	for (
		let currentElement: Element | null = element;
		currentElement != null;
		currentElement = currentElement?.parentElement
	) {
		if (
			currentElement.attributes?.getNamedItem('x-mission-io-annotations-root')
				?.nodeValue === 'true'
		) {
			return currentElement
		}
	}
	return null
}

/**
 * getNodeMarkdownStartIndex - get the markdown start index of the node. Returns null if a markdown start index could not be determined
 *
 * @param {globalThis.Node | null} node? - the node to check the markdown start index for
 *
 * @return {number | null}
 */
export function getNodeMarkdownStartIndex(
	node?: globalThis.Node | null
): number | null {
	let element = node

	let value = null
	while (element && value == null) {
		if (element instanceof Element) {
			value = element?.attributes?.getNamedItem(
				'x-mission-io-annotation-start-index'
			)?.nodeValue
		}

		element = element.parentElement
	}
	if (value == null) {
		return null
	}

	const startIndex = parseInt(value)
	if (Number.isNaN(startIndex)) {
		return null
	}
	return startIndex
}
